/**
 * useFacilities — shared, staged CRUD hook over the Schedule location
 * SoT.
 *
 * Both UW Information → Schedule of Location and Schedule →
 * Facility Schedule subscribe through this hook to the same submission-
 * scoped location list. An edit in one section instantly shows up in
 * the other because every hook instance reads/writes through a
 * module-level pubsub keyed by `submissionId`.
 *
 * Data source: the real `/v2/submissions/{id}/locations` backend
 * (orion-backend PR #261). `endpoints/facilities.ts` adapts the FE
 * `Facility` shape to/from the backend location row.
 *
 * Staged editing (matches the other submission sections):
 *   - `create` / `update` / `remove` mutate ONLY the in-memory working
 *     copy — nothing hits the network.
 *   - `save()` diffs the working copy against the last-saved baseline
 *     and flushes every change (POST new rows, PATCH changed rows,
 *     DELETE removed rows). The Schedule page's Save button calls it.
 *   - `isDirty` reports whether there are unsaved changes.
 *
 * State machine:
 *   { status: "loading" }
 *   → { status: "ready",   data: Facility[] }
 *   → { status: "error",   error: Error }
 *
 * Caller contract on `submissionId`:
 *   - A real id → loads that submission's locations from the backend.
 *   - `""` → no saved submission yet; the location endpoint can't be
 *     called. Callers should gate the Schedule UI until there is a
 *     real id.
 *   - `undefined` → hook stays in "loading" forever; callers should
 *     gate render until they have a value.
 */
import { useCallback, useEffect, useState } from "react";
import {
    createFacility as createFacilityApi,
    deleteFacility as deleteFacilityApi,
    hasPersistableLocationFields,
    type LocationComputedFields,
    listFacilities as listFacilitiesApi,
    patchFacility as patchFacilityApi,
} from "../api";
import { noteArchiveTransition } from "../domain/facility-archive";
import type { Facility, FacilityWritePayload } from "../types/facility";
import { emptyFacility } from "../types/facility";

export type FacilitiesQueryState =
    | { status: "loading" }
    | { status: "ready"; data: Facility[] }
    | { status: "error"; error: Error };

export interface SaveFacilitiesOptions {
    /** Row id → FE-computed pricing values to persist alongside the row. */
    computedById?: Map<string, LocationComputedFields>;
    /** Also PATCH the computed values of rows with no field changes. Set
     *  when an input outside the rows (the Relativity factors) moved every
     *  row's premium; otherwise the backend keeps the old per-location
     *  premiums and the quote sums them (OR-1654). */
    syncComputed?: boolean;
}

export interface UseFacilitiesResult {
    state: FacilitiesQueryState;
    /** Stage a new row locally (temp id). Not persisted until `save()`. */
    create: (payload?: FacilityWritePayload) => Promise<Facility | null>;
    /** Stage a field change locally. Not persisted until `save()`. */
    update: (id: string, payload: FacilityWritePayload) => Promise<void>;
    /** Stage a row removal locally. Not persisted until `save()`. */
    remove: (id: string) => Promise<void>;
    /** Flush every staged change (create / update / delete) to the
     *  backend. Returns false if any call failed.
     *  `opts.computedById` maps a row id → its FE-computed pricing values
     *  (equivalency, premiums, …); when present they ride along on every
     *  POST/PATCH so the backend can drive its quote calc (Henry, jun-23).
     *  The Schedule page supplies it; other callers (Operational) omit it. */
    save: (opts?: SaveFacilitiesOptions) => Promise<boolean>;
    /** True when the working copy differs from the last-saved state. */
    isDirty: boolean;
    /** Background refetch of the server list into the shared store (no
     *  loading flash). Lets a section re-sync facilities created/deleted
     *  elsewhere — e.g. via the Acuity direct POST — without a page refresh
     *  (mirrors useOperationalStaffing.reload, OR-1297). */
    reload: () => Promise<void>;
}

const DRAFT_SCOPE = "draft";
/** Prefix for client-side ids on rows that haven't been POSTed yet. */
const TEMP_ID_PREFIX = "local-";

/** Writable columns. `emptyFacility()` enumerates exactly the
 *  `FacilityWritePayload` keys, so it doubles as the canonical list. */
const WRITABLE_KEYS = Object.keys(emptyFacility()) as (keyof FacilityWritePayload)[];

// ── Module-level pubsub ──────────────────────────────────────────
// One state slot per submission scope. All hook instances pointing at
// the same scope share these slots, so an update in one section
// re-renders the other.

const stateByKey = new Map<string, FacilitiesQueryState>();
/** Last-saved server snapshot per scope — the diff baseline for `save()`. */
const baselineByKey = new Map<string, Facility[]>();
const subscribersByKey = new Map<string, Set<(s: FacilitiesQueryState) => void>>();
const inflightLoad = new Map<string, Promise<void>>();

function scopeOf(submissionId: string): string {
    return submissionId.trim() || DRAFT_SCOPE;
}

function isTempId(id: string): boolean {
    return id.startsWith(TEMP_ID_PREFIX);
}

/** Shallow per-row clone — `Facility` is a flat string record, so this
 *  is enough to detach the baseline from the working copy. */
function snapshot(rows: Facility[]): Facility[] {
    return rows.map((f) => ({ ...f }));
}

function sameData(a: Facility[], b: Facility[]): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}

/** Full editable payload for a brand-new row (POST body). */
function toWritePayload(f: Facility): FacilityWritePayload {
    const out: Record<string, unknown> = {};
    for (const k of WRITABLE_KEYS) out[k] = f[k];
    return out as FacilityWritePayload;
}

/** Read-only fields the server owns and stamps on its own — the client
 *  never writes them, so a save must ADOPT them from the response rather
 *  than let the optimistic local row win (OR-1442).
 *
 *  `duplicateSubmissionId` is the one that mattered: the backend runs
 *  duplicate detection inside the location POST and stamps the flag on the
 *  row it returns, but `save()` used to keep only `id` + timestamps, so the
 *  flag stayed invisible until the next full list load (i.e. a page
 *  refresh). Neither key is in `WRITABLE_KEYS`, so adopting them can't make
 *  `diffPayload` see a change and mark the store dirty. */
function serverOwned(row: Facility): Pick<Facility, "duplicateSubmissionId" | "catalogFacilityId"> {
    return {
        duplicateSubmissionId: row.duplicateSubmissionId,
        catalogFacilityId: row.catalogFacilityId,
    };
}

/** Only the columns that changed vs the baseline (PATCH body). */
function diffPayload(base: Facility, cur: Facility): FacilityWritePayload {
    const diff: Record<string, unknown> = {};
    for (const k of WRITABLE_KEYS) {
        if (base[k] !== cur[k]) diff[k] = cur[k];
    }
    return diff as FacilityWritePayload;
}

function emit(key: string, next: FacilitiesQueryState): void {
    stateByKey.set(key, next);
    const subs = subscribersByKey.get(key);
    if (!subs) return;
    subs.forEach((cb) => cb(next));
}

function subscribe(key: string, cb: (s: FacilitiesQueryState) => void): () => void {
    let set = subscribersByKey.get(key);
    if (!set) {
        set = new Set();
        subscribersByKey.set(key, set);
    }
    set.add(cb);
    return () => {
        set!.delete(cb);
    };
}

/** Replace the data array in place via a transform — preserves the
 *  "ready" status wrapper and emits to subscribers. No-op when the
 *  scope hasn't loaded yet. */
function mutateData(key: string, transform: (prev: Facility[]) => Facility[]): void {
    const current = stateByKey.get(key);
    if (current?.status !== "ready") return;
    emit(key, { status: "ready", data: transform(current.data) });
}

function load(submissionId: string): Promise<void> {
    const key = scopeOf(submissionId);
    const existing = inflightLoad.get(key);
    if (existing) return existing;
    const promise = listFacilitiesApi(submissionId)
        .then((data) => {
            // The freshly-loaded list is both the working copy and the
            // baseline `save()` diffs against.
            baselineByKey.set(key, snapshot(data));
            emit(key, { status: "ready", data });
        })
        .catch((error: unknown) => {
            emit(key, {
                status: "error",
                error: error instanceof Error ? error : new Error("Failed to load facilities"),
            });
        })
        .finally(() => {
            inflightLoad.delete(key);
        });
    inflightLoad.set(key, promise);
    return promise;
}

// ── Hook ─────────────────────────────────────────────────────────

export function useFacilities(submissionId: string | undefined): UseFacilitiesResult {
    const key = submissionId === undefined ? null : scopeOf(submissionId);

    const [state, setState] = useState<FacilitiesQueryState>(() => {
        if (key === null) return { status: "loading" };
        return stateByKey.get(key) ?? { status: "loading" };
    });

    useEffect(() => {
        if (key === null || submissionId === undefined) return;
        // Subscribe FIRST so we don't miss the resolution of an
        // already-in-flight load triggered by a sibling hook.
        const unsubscribe = subscribe(key, setState);
        // Seed local state from cache if present.
        const cached = stateByKey.get(key);
        if (cached) setState(cached);
        // Trigger a load if we haven't yet (or the last attempt errored).
        if (!cached || cached.status === "error") {
            emit(key, { status: "loading" });
            void load(submissionId);
        }
        return unsubscribe;
    }, [key, submissionId]);

    // ── Staged mutations — local only, no network ────────────
    const create = useCallback<UseFacilitiesResult["create"]>(
        async (payload = {}) => {
            if (submissionId === undefined || key === null) return null;
            const row = {
                ...emptyFacility(),
                ...payload,
                id: `${TEMP_ID_PREFIX}${crypto.randomUUID()}`,
                submissionId,
                catalogFacilityId: "",
                duplicateSubmissionId: "",
                createdAt: "",
                updatedAt: "",
            } as Facility;
            mutateData(key, (prev) => [...prev, row]);
            return row;
        },
        [submissionId, key],
    );

    const update = useCallback<UseFacilitiesResult["update"]>(
        async (id, payload) => {
            if (key === null) return;
            const isArchiveToggle = payload.isExcluded !== undefined;
            // Archive / restore (OR-1431): stamp the session sequence so the
            // row sorts to the right spot at the bottom of the table even
            // before Save (nothing has hit the network yet, so `updatedAt`
            // still holds the pre-archive value). Done here rather than at the
            // callsites so every page's ⋮ menu inherits the ordering.
            if (isArchiveToggle) {
                noteArchiveTransition(id, payload.isExcluded as boolean);
            }
            mutateData(key, (prev) =>
                prev.map((f) => {
                    if (f.id !== id) return f;
                    // An archived facility's data is frozen until it's restored
                    // (OR-1431) — the row renders read-only, and this is the
                    // invariant behind that rendering. Enforced here rather
                    // than per-cell so no control, keyboard path, or
                    // not-yet-written table can slip an edit through; the only
                    // write an archived row accepts is its own restore.
                    if (f.isExcluded && !isArchiveToggle) return f;
                    return { ...f, ...payload };
                }),
            );
        },
        [key],
    );

    const remove = useCallback<UseFacilitiesResult["remove"]>(
        async (id) => {
            if (key === null) return;
            mutateData(key, (prev) => prev.filter((f) => f.id !== id));
        },
        [key],
    );

    // ── Flush staged changes to the backend ──────────────────
    const save = useCallback<UseFacilitiesResult["save"]>(async (opts) => {
        if (submissionId === undefined || key === null) return false;
        const current = stateByKey.get(key);
        // Nothing loaded → nothing to save. A computed sync can't be skipped,
        // though: reporting success would let the caller persist the new
        // Relativity while the stored premiums stay stale (OR-1654).
        if (current?.status !== "ready") return !opts?.syncComputed;
        const working = current.data;
        const baseline = baselineByKey.get(key) ?? [];
        if (sameData(working, baseline) && !opts?.syncComputed) return true; // clean
        // The calcs feeding `computedById` can lag the list by a render, so a
        // row (persisted or new) may have no values yet. Fail before writing
        // anything rather than save a premium that doesn't match Relativity.
        if (opts?.syncComputed && working.some((r) => !opts.computedById?.has(r.id))) return false;

        try {
            // Deletes: baseline rows no longer in the working copy.
            // (Temp-id rows are never in the baseline, so a created-then-
            // removed row simply vanishes with no API call.)
            const deletes = baseline.filter((b) => !working.some((w) => w.id === b.id));
            await Promise.all(deletes.map((d) => deleteFacilityApi(submissionId, d.id)));

            // Creates / updates, preserving row order.
            const computedById = opts?.computedById;
            const nextRows: Facility[] = [];
            for (const row of working) {
                if (isTempId(row.id)) {
                    const created = await createFacilityApi(submissionId, toWritePayload(row), computedById?.get(row.id));
                    // Keep the working values (incl. FE-only fields the
                    // backend doesn't echo); adopt the server id + stamps
                    // + the server-owned read-only fields (OR-1442).
                    nextRows.push({
                        ...row,
                        id: created.id,
                        ...serverOwned(created),
                        createdAt: created.createdAt,
                        updatedAt: created.updatedAt,
                    });
                    continue;
                }
                const base = baseline.find((b) => b.id === row.id);
                const diff = base ? diffPayload(base, row) : {};
                // Only hit the network when the diff touches a column the
                // backend can store. A diff of only FE-only fields
                // (e.g. providerNumber) maps to an empty `{}` body, so we
                // skip the no-op PATCH and just keep the working values.
                if (Object.keys(diff).length > 0 && hasPersistableLocationFields(diff)) {
                    const patched = await patchFacilityApi(submissionId, row.id, diff, computedById?.get(row.id));
                    nextRows.push({ ...row, ...serverOwned(patched), updatedAt: patched.updatedAt });
                } else if (opts?.syncComputed && computedById?.has(row.id)) {
                    // Computed-only PATCH: the row itself is unchanged.
                    const patched = await patchFacilityApi(submissionId, row.id, {}, computedById.get(row.id));
                    nextRows.push({ ...row, ...serverOwned(patched), updatedAt: patched.updatedAt });
                } else {
                    nextRows.push(row);
                }
            }

            // The table stays editable while the requests run. Rows edited,
            // added or removed meanwhile keep their latest state (with temp
            // ids mapped to the created ones) plus the saved server fields,
            // instead of being overwritten by the `working` snapshot; the
            // baseline is what was saved, so those edits stay dirty for the
            // next save.
            const latest = stateByKey.get(key);
            let data = nextRows;
            if (latest?.status === "ready" && latest.data !== working) {
                const idMap = new Map(working.map((w, i) => [w.id, nextRows[i].id]));
                const savedById = new Map(nextRows.map((n) => [n.id, n]));
                data = latest.data.map((r) => {
                    const saved = savedById.get(idMap.get(r.id) ?? r.id);
                    return saved ? { ...r, id: saved.id, ...serverOwned(saved), createdAt: saved.createdAt, updatedAt: saved.updatedAt } : r;
                });
            }
            baselineByKey.set(key, snapshot(nextRows));
            emit(key, { status: "ready", data });
            return true;
        } catch (error) {
            console.warn("saveFacilities failed:", error);
            return false;
        }
    }, [submissionId, key]);

    const baseline = key === null ? undefined : baselineByKey.get(key);
    const isDirty = state.status === "ready" && baseline !== undefined ? !sameData(state.data, baseline) : false;

    // Background refetch into the shared store (`load` emits "ready" without a
    // "loading" flash and is deduped via inflightLoad). Callers gate on !isDirty
    // so a reload never clobbers unsaved staged edits.
    const reload = useCallback(async () => {
        if (submissionId === undefined || key === null) return;
        await load(submissionId);
    }, [submissionId, key]);

    return { state, create, update, remove, save, isDirty, reload };
}
